import { useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSurface } from "../lib/useSurface.js";
import { Frame } from "../components/Frame.js";
import { Icon } from "../components/Icon.js";
import { useCloseTo } from "../lib/useCloseTo.js";
import { enqueueCapture } from "../lib/offline.js";
import { uploads, type UploadFileSpec } from "../lib/api.js";
import { mmss } from "../lib/demo.js";

interface Shot {
  url: string;
  blob: Blob;
  /** optional narration recorded for this step */
  audio?: Blob;
}

/**
 * Screen B — image flow. Each step is a still (<input capture>) plus an auto-started audio
 * narration (MediaRecorder). Captured stills are filmstripped; "次へ" advances to processing.
 */
export function CaptureImage() {
  useSurface("field");
  const navigate = useNavigate();
  const closeTo = useCloseTo();
  const fileRef = useRef<HTMLInputElement>(null);
  const audioRecRef = useRef<MediaRecorder | null>(null);
  const audioChunks = useRef<Blob[]>([]);
  const [shots, setShots] = useState<Shot[]>([]);
  const [recording, setRecording] = useState(false);

  /** Stop the in-flight narration recorder and resolve with its blob (null if none). */
  function stopNarration(): Promise<Blob | null> {
    const rec = audioRecRef.current;
    if (!rec || rec.state !== "recording") return Promise.resolve(null);
    return new Promise((resolve) => {
      const prevOnStop = rec.onstop;
      rec.onstop = (ev) => {
        if (typeof prevOnStop === "function") prevOnStop.call(rec, ev);
        const blob = audioChunks.current.length
          ? new Blob(audioChunks.current, { type: audioChunks.current[0]?.type || "audio/webm" })
          : null;
        resolve(blob);
      };
      rec.stop();
    });
  }

  async function startNarration() {
    if (!navigator.mediaDevices?.getUserMedia) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioChunks.current = [];
      const rec = new MediaRecorder(stream);
      rec.ondataavailable = (e) => e.data.size && audioChunks.current.push(e.data);
      rec.onstop = () => stream.getTracks().forEach((t) => t.stop());
      rec.start();
      audioRecRef.current = rec;
      setRecording(true);
    } catch {
      /* mic denied — image-only is still fine */
    }
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    // Close out the previous step's narration and attach it to that shot.
    const prevAudio = await stopNarration();
    setRecording(false);
    if (prevAudio) {
      setShots((s) => (s.length ? s.map((sh, i) => (i === s.length - 1 ? { ...sh, audio: prevAudio } : sh)) : s));
    }
    setShots((s) => [...s, { url: URL.createObjectURL(file), blob: file }]);
    void startNarration();
  }

  async function finish() {
    const lastAudio = await stopNarration();
    setRecording(false);
    // Attach the final narration to the last shot before uploading.
    const finalShots = lastAudio && shots.length
      ? shots.map((sh, i) => (i === shots.length - 1 ? { ...sh, audio: lastAudio } : sh))
      : shots;
    setShots(finalShots);

    // Upload directly: ONE manual batching every image + its narration audio (image→audio order).
    // Only fall back to the offline queue if the handshake fails — enqueuing per still AND
    // uploading would create duplicate (per-still) untitled draft manuals.
    let manualId = `local_${Date.now().toString(36)}`;
    let uploaded = false;
    try {
      const files: UploadFileSpec[] = [];
      const blobs: Blob[] = [];
      for (const shot of finalShots) {
        files.push({ kind: "image", mimeType: shot.blob.type || "image/jpeg" });
        blobs.push(shot.blob);
        if (shot.audio) {
          files.push({ kind: "audio", mimeType: shot.audio.type || "audio/webm" });
          blobs.push(shot.audio);
        }
      }
      if (files.length) {
        const init = await uploads.init({ sourceType: "image", files });
        manualId = init.manualId;
        await Promise.all(init.uploads.map((t, i) => uploads.put(t.mediaId, blobs[i])));
        const primary = init.uploads.find((u) => u.kind === "image") ?? init.uploads[0];
        if (primary) await uploads.complete(primary.mediaId, { manualId, primaryMediaId: primary.mediaId });
        uploaded = true;
      }
    } catch {
      /* offline / API unreachable — fall back to the queue below */
    }

    if (!uploaded) {
      for (const shot of finalShots) {
        try {
          await enqueueCapture({ filename: `still_${Date.now()}.jpg`, contentType: shot.blob.type || "image/jpeg", source: { sourceType: "image" }, blob: shot.blob });
        } catch {
          /* IndexedDB unavailable */
        }
      }
    }
    navigate(`/processing/${manualId}`);
  }

  return (
    <div className="cap-shell" style={{ height: "100dvh", display: "flex", flexDirection: "column" }}>
      <h1 className="sr-only">画像撮影</h1>
      <input ref={fileRef} type="file" accept="image/*" capture="environment" hidden onChange={onFile} />

      <div className="viewfinder">
        <span className="vf-corner vf-tl" />
        <span className="vf-corner vf-tr" />
        <span className="vf-corner vf-bl" />
        <span className="vf-corner vf-br" />
        <div className="frame" style={{ width: "min(46vw,300px)", border: 0, background: "none", boxShadow: "none" }}>
          {shots.length ? (
            <img src={shots[shots.length - 1].url} alt="直近の撮影" style={{ borderRadius: 12, boxShadow: "0 30px 70px -10px rgba(0,0,0,.7)" }} />
          ) : (
            <div className="subj" style={{ boxShadow: "0 30px 70px -10px rgba(0,0,0,.7)" }} />
          )}
        </div>

        <div className="topstrip" style={{ position: "absolute", top: 0, left: 0, right: 0, zIndex: 10, display: "flex", alignItems: "center", gap: 12, padding: "max(16px, env(safe-area-inset-top)) max(20px, env(safe-area-inset-right)) 16px max(20px, env(safe-area-inset-left))" }}>
          <Link className="btn btn--ghost" to={closeTo} aria-label="閉じる" style={{ minHeight: 56, minWidth: 56, padding: "8px 14px", borderRadius: 12, background: "rgba(0,0,0,.4)" }}>
            <Icon name="close" size={20} />
          </Link>
          <div className="grow">
            <div className="kicker">画像フロー — 手順を1枚ずつ</div>
            <div className="stepper" style={{ marginTop: 5 }}>
              {Array.from({ length: Math.max(3, shots.length) }, (_, i) => (
                <span key={i} className={`seg${i < shots.length ? " on" : ""}`} />
              ))}
              <span className="mono" style={{ marginLeft: 8 }}>
                手順 {shots.length} 撮影済
              </span>
            </div>
          </div>
          <span className="statuspill" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            <Icon name="offline" size={14} /> あとで自動送信
          </span>
        </div>

        <div className="guide" style={{ position: "absolute", top: "42%", left: 0, right: 0, textAlign: "center", pointerEvents: "none", padding: "0 30px" }}>
          <div className="kicker" style={{ color: "var(--hi)" }}>
            撮影 → 自動で録音がはじまります
          </div>
          <div className="display jp" style={{ marginTop: 8, fontSize: "clamp(20px,4.5vw,30px)" }}>
            「この手順を説明してください」
          </div>
        </div>
      </div>

      <div
        className="filmstrip"
        style={{ position: "absolute", left: 0, right: 0, bottom: 148, display: "flex", gap: 10, padding: "0 20px", overflowX: "auto", zIndex: 8 }}
      >
        {shots.map((s, i) => (
          <div key={i} className="thumb" style={{ width: 78, flex: "0 0 auto" }}>
            <Frame src={s.url} className="thumb" style={{ border: 0 }} />
            <span className="tc">{String(i + 1).padStart(2, "0")}</span>
            <span className="dot dot--blue" aria-hidden="true" />
          </div>
        ))}
      </div>

      <div
        className="controls"
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          padding: "18px 24px calc(24px + env(safe-area-inset-bottom))",
          display: "grid",
          gridTemplateColumns: "1fr auto 1fr",
          alignItems: "center",
          gap: 14,
          background: "linear-gradient(0deg,rgba(0,0,0,.72),transparent)",
        }}
      >
        <div className="narr" style={{ display: "flex", alignItems: "center", gap: 10, background: "rgba(0,0,0,.42)", padding: "10px 13px", borderRadius: 14, backdropFilter: "blur(8px)" }}>
          <div className="wave" aria-hidden="true">
            {Array.from({ length: 7 }, (_, i) => (
              <i key={i} />
            ))}
          </div>
          <span className="mono" style={{ fontSize: 12, color: "var(--hi)" }}>
            {recording ? `録音中 ${mmss(0)}` : "撮影で録音"}
          </span>
        </div>
        <button className="shutter" aria-label="撮影" onClick={() => fileRef.current?.click()} />
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button className="btn btn--hi" onClick={finish} disabled={shots.length === 0} style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            次へ <Icon name="arrow-right" size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
